/* Front Desk — single-screen POS / counter console for admin & finance.

   One search bar handles every front-of-house scenario:
     • Student walks in to pay → search by name/phone → record payment (cash, card,
       transfer, e-wallet) across one or more invoices in a single screen.
     • Existing parent wants to add a class → search → "Add Course" deep-links to
       the Walk-in Enrol wizard with the parent pre-filled.
     • Brand-new family → no match → "Start New Enrolment" → Walk-in Enrol wizard.

   Inspired by Square / Shopify POS / Stripe Dashboard. */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { errMsg, fmtRM } from "@/lib/api";
import { PageHeader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  Search, UserPlus, CreditCard, Banknote, ArrowRight, CheckCircle2,
  Music4, Calendar, AlertCircle, Receipt,
} from "lucide-react";

export default function FrontDeskPage() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [payingFor, setPayingFor] = useState(null); // student object

  // Debounced live search
  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/admin/front-desk/search?q=${encodeURIComponent(q)}`);
        setResults(data.results || []);
      } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const newEnrolment = () => navigate("/admin/walk-in-enrol");

  return (
    <div data-testid="front-desk-page">
      <PageHeader
        title="Front Desk"
        description="Counter / POS — search a student or parent to take payment, add a course, or open their record. Empty search? Start a new walk-in enrolment." />

      {/* Search */}
      <Card className="mb-5">
        <CardContent className="p-5">
          <Label className="text-xs font-semibold tracking-wide uppercase text-muted-foreground">
            Student name · Parent name · Parent phone · Parent email
          </Label>
          <div className="relative mt-2">
            <Search className="h-5 w-5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              autoFocus value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Start typing… (e.g. emma, 012-345, mei wong)"
              className="pl-10 text-base h-12"
              data-testid="front-desk-search" />
          </div>
        </CardContent>
      </Card>

      {/* Results / empty state */}
      {!q.trim() ? (
        <Card className="border-dashed">
          <CardContent className="p-8 text-center space-y-3">
            <Search className="h-10 w-10 mx-auto text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">
              Type any name or phone above. Existing student or parent? Their unpaid invoices appear instantly with a one-click payment button.
            </p>
            <p className="text-sm text-muted-foreground">Walking in fresh, no record yet?</p>
            <Button onClick={newEnrolment} data-testid="new-enrolment-cta">
              <UserPlus className="h-4 w-4 mr-1.5" />Start New Enrolment
            </Button>
          </CardContent>
        </Card>
      ) : loading ? (
        <p className="text-sm text-muted-foreground py-6 text-center">Searching…</p>
      ) : results.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="p-8 text-center space-y-3">
            <AlertCircle className="h-10 w-10 mx-auto text-amber-500/60" />
            <p className="font-medium">No student or parent matches "{q}"</p>
            <p className="text-xs text-muted-foreground">Looks like a brand-new family. Start their enrolment here:</p>
            <Button onClick={newEnrolment} data-testid="no-match-new-enrolment">
              <UserPlus className="h-4 w-4 mr-1.5" />Start New Enrolment for "{q}"
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3 stagger-in">
          {results.map((s) => (
            <StudentResultCard key={s.student_id} s={s}
              onTakePayment={() => setPayingFor(s)}
              onAddCourse={() => navigate(`/admin/walk-in-enrol?student_id=${s.student_id}`)}
              onOpen={() => navigate(`/admin/students/${s.student_id}`)} />
          ))}
        </div>
      )}

      {/* Counter Payment dialog */}
      {payingFor && (
        <CounterPaymentDialog
          student={payingFor} onClose={() => setPayingFor(null)}
          onPaid={() => { setPayingFor(null); /* refresh */
            api.get(`/admin/front-desk/search?q=${encodeURIComponent(q)}`)
              .then((r) => setResults(r.data.results || []));
          }} />
      )}
    </div>
  );
}

// -------------- Result card --------------
function StudentResultCard({ s, onTakePayment, onAddCourse, onOpen }) {
  const hasOutstanding = (s.outstanding_total || 0) > 0.01;
  const parent = s.parents?.[0];
  return (
    <Card className="overflow-hidden" data-testid={`student-card-${s.student_id}`}>
      <CardContent className="p-5">
        <div className="flex flex-wrap items-start gap-4">
          <div className="flex-1 min-w-[200px] space-y-1">
            <div className="flex items-center gap-2 flex-wrap">
              <Music4 className="h-4 w-4 text-secondary" />
              <p className="font-display text-lg font-semibold" data-testid={`student-name-${s.student_id}`}>
                {s.student_name}
              </p>
              {s.student_status && s.student_status !== "active" && (
                <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-muted">
                  {s.student_status}
                </span>
              )}
            </div>
            {parent && (
              <p className="text-xs text-muted-foreground">
                Parent: <b>{parent.full_name}</b> · {parent.phone || "no phone"} · {parent.email || "no email"}
              </p>
            )}
            {s.schedules.length > 0 ? (
              <p className="text-xs text-muted-foreground flex flex-wrap gap-x-2 gap-y-0.5">
                <Calendar className="h-3 w-3 mt-[2px]" />
                {s.schedules.map((sc, i) => (
                  <span key={i}>
                    {sc.course_name} · {sc.day !== undefined ? ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"][sc.day] : ""} {sc.start_time} · {sc.teacher_name}
                    {i < s.schedules.length - 1 ? " · " : ""}
                  </span>
                ))}
              </p>
            ) : (
              <p className="text-xs text-muted-foreground italic">No active class schedules</p>
            )}
          </div>

          <div className="text-right">
            <p className={`text-2xl font-bold ${hasOutstanding ? "text-[#D90429]" : "text-emerald-600"}`}>
              {fmtRM(s.outstanding_total)}
            </p>
            <p className="text-xs text-muted-foreground">
              {s.unpaid_count > 0
                ? `${s.unpaid_count} unpaid invoice${s.unpaid_count > 1 ? "s" : ""}`
                : "All clear ✓"}
            </p>
          </div>
        </div>

        <div className="flex gap-2 flex-wrap mt-4 pt-3 border-t">
          {hasOutstanding && (
            <Button size="sm" onClick={onTakePayment}
              data-testid={`take-payment-${s.student_id}`}
              className="bg-emerald-600 hover:bg-emerald-700">
              <CreditCard className="h-4 w-4 mr-1.5" />
              Take Payment ({fmtRM(s.outstanding_total)})
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={onAddCourse}
            data-testid={`add-course-${s.student_id}`}>
            <UserPlus className="h-4 w-4 mr-1.5" />Add Course
          </Button>
          <Button size="sm" variant="ghost" onClick={onOpen}
            data-testid={`open-record-${s.student_id}`}>
            Open Record <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// -------------- Counter Payment Dialog --------------
function CounterPaymentDialog({ student, onClose, onPaid }) {
  const [selected, setSelected] = useState(() =>
    Object.fromEntries(student.invoices.map((i) => [i.id, true])));
  const [method, setMethod] = useState("cash");
  const [refNo, setRefNo] = useState("");
  const [busy, setBusy] = useState(false);

  const lines = useMemo(() => student.invoices.filter((i) => selected[i.id]), [student, selected]);
  const total = useMemo(() => lines.reduce((sum, i) => sum + (i.outstanding || 0), 0), [lines]);

  const submit = async () => {
    if (lines.length === 0) { toast.error("Select at least one invoice"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/payments/bulk-record", {
        payments: lines.map((i) => ({ invoice_id: i.id, amount: i.outstanding })),
        payment_method: method,
        reference_no: refNo || null,
      });
      toast.success(`✓ Recorded ${data.recorded} payment(s) · Total RM ${data.total.toFixed(2)}`, { duration: 6000 });
      onPaid && onPaid();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="counter-payment-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Receipt className="h-5 w-5 text-secondary" />
            Counter Payment — {student.student_name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 max-h-[55vh] overflow-y-auto">
          {student.invoices.map((inv) => (
            <label key={inv.id}
              className={`flex items-center gap-3 p-3 rounded-md border cursor-pointer hover:bg-accent ${selected[inv.id] ? "bg-accent/60 border-secondary" : ""}`}
              data-testid={`pay-row-${inv.id}`}>
              <Checkbox
                checked={!!selected[inv.id]}
                onCheckedChange={(v) => setSelected((s) => ({ ...s, [inv.id]: !!v }))}
                data-testid={`pay-toggle-${inv.id}`} />
              <div className="flex-1 min-w-0">
                <p className="font-mono text-xs">{inv.invoice_number}</p>
                <p className="text-sm">{inv.invoice_month} · {inv.invoice_type}</p>
                <p className="text-[10px] text-muted-foreground">Due {inv.due_date}</p>
              </div>
              <p className="font-bold text-[#D90429]">{fmtRM(inv.outstanding)}</p>
            </label>
          ))}
        </div>

        <div className="rounded-md bg-secondary/10 p-3 border-2 border-secondary/30 text-center">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Selected total</p>
          <p className="text-3xl font-bold text-secondary" data-testid="selected-total">{fmtRM(total)}</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Payment method</Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger data-testid="counter-method-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="cash">💵 Cash</SelectItem>
                <SelectItem value="card">💳 Card (terminal)</SelectItem>
                <SelectItem value="bank_transfer">🏦 Bank transfer</SelectItem>
                <SelectItem value="ewallet">📱 E-wallet (TNG / GrabPay)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Reference no (optional)</Label>
            <Input value={refNo} onChange={(e) => setRefNo(e.target.value)}
              placeholder="Receipt / txn id" data-testid="counter-ref-input" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="counter-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy || total < 0.01}
            data-testid="counter-confirm"
            className="bg-emerald-600 hover:bg-emerald-700">
            {busy ? "Recording…" :
              <><CheckCircle2 className="h-4 w-4 mr-1.5" />Record {fmtRM(total)} {method}</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
